import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useStore } from '../data/store';
import { supabase } from '../lib/supabase';
import { ArrowLeft, Settings, BarChart3 } from 'lucide-react';

const PlayerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { players } = useStore();
  const [playerStats, setPlayerStats] = useState<any[]>([]);
  const [selectedSeasonIdx, setSelectedSeasonIdx] = useState(0);
  const [careerStats, setCareerStats] = useState<any>(null);

  const player = players.find(p => p.id === id);

  useEffect(() => {
    const fetchStats = async () => {
      if (!id) return;

      try {
        const { data, error } = await supabase
          .from('player_statistics')
          .select('*')
          .eq('player_id', id)
          .order('season', { ascending: false });

        if (error) throw error;
        setPlayerStats(data || []);

        if (data && data.length > 0) {
          setSelectedSeasonIdx(0);
        }

        const { data: careerData, error: careerError } = await supabase
          .from('player_career_stats')
          .select('*')
          .eq('player_id', id)
          .maybeSingle();

        if (careerError) throw careerError;
        setCareerStats(careerData);
      } catch (error) {
        console.error('Error fetching player stats:', error);
      }
    };

    fetchStats();
  }, [id]);

  if (!player) {
    return (
      <div className="min-h-screen flex flex-col bg-[#111111]">
        <Navbar />
        <main className="flex-1 mt-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => navigate('/players')}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Roster
            </button>
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">Team member not found.</p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#111111]">
      <Navbar />
      
      <main className="flex-1 mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/players')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Roster
          </button>

          <div className="bg-[#222222] rounded-lg overflow-hidden shadow-lg border border-white/10">
            {player.role === 'gameday' ? (
              // Gameday Operator Layout
              <div className="md:flex items-stretch">
                <div className="md:w-1/2">
                  <div className="relative aspect-square">
                    <img 
                      src={player.image} 
                      alt={player.name} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 md:hidden">
                      <h1 className="text-3xl font-bold text-white">{player.name}</h1>
                      <div className="flex items-center gap-2 text-gray-300 mt-2">
                        <Settings className="w-5 h-5" />
                        <span>{player.title || 'Gameday Staff'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="md:w-1/2 p-8 flex flex-col justify-center">
                  <div className="hidden md:block mb-6">
                    <h1 className="text-4xl font-bold text-white mb-4">{player.name}</h1>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-cardinal/10 rounded-lg">
                      <Settings className="w-5 h-5 text-cardinal" />
                      <span className="text-lg font-medium text-white">{player.title || 'Gameday Staff'}</span>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="flex flex-col gap-4">
                      <div className="p-6 bg-[#1A1A1A] rounded-lg border border-white/5">
                        <p className="text-gray-300 leading-relaxed">
                          {player.name} is an essential member of our gameday operations team, 
                          helping ensure smooth game experiences for players and fans alike.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Player Layout
              <div className="md:flex">
                <div className="md:w-1/2">
                  <div className="relative aspect-square">
                    <img 
                      src={player.image} 
                      alt={player.name} 
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 md:hidden">
                      <h1 className="text-3xl font-bold text-white">{player.name}</h1>
                      <div className="flex items-center gap-2 text-gray-300 mt-2">
                        <span>#{player.number}</span>
                        <span className="mx-2">•</span>
                        <span>{Array.isArray(player.positions) ? player.positions.join(' / ') : player.positions}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="md:w-1/2 p-8">
                  <div className="hidden md:block">
                    <h1 className="text-4xl font-bold text-white mb-4">{player.name}</h1>
                    <div className="flex items-center gap-4 text-gray-300 mb-6">
                      <span className="text-2xl font-bold text-cardinal">#{player.number}</span>
                      <span className="text-lg">{Array.isArray(player.positions) ? player.positions.join(' / ') : player.positions}</span>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h2 className="text-xl font-semibold text-white mb-2">About</h2>
                      <p className="text-gray-300 leading-relaxed">{player.bio || 'Bio coming soon...'}</p>
                    </div>

                    <div className="p-4 bg-[#1A1A1A] rounded-lg inline-block">
                      <p className="text-gray-300">
                        <span className="text-cardinal font-medium">High School:</span>{' '}
                        {player.hometown || 'Not listed'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {player.role === 'player' && (
            <div className="mt-8 space-y-8">
              {careerStats && (
                <div className="bg-gradient-to-br from-cardinal/20 to-cardinal/5 rounded-lg overflow-hidden shadow-lg border border-cardinal/30 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <BarChart3 className="w-6 h-6 text-cardinal" />
                    <h2 className="text-2xl font-bold text-white">Career Totals</h2>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">Batting</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                        <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                          <p className="text-gray-400 text-sm uppercase">Games</p>
                          <p className="text-2xl font-bold text-white">{careerStats.total_games_played}</p>
                        </div>
                        <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                          <p className="text-gray-400 text-sm uppercase">AVG</p>
                          <p className="text-2xl font-bold text-white">{Number(careerStats.career_batting_average).toFixed(3)}</p>
                        </div>
                        <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                          <p className="text-gray-400 text-sm uppercase">At Bats</p>
                          <p className="text-2xl font-bold text-white">{careerStats.total_at_bats}</p>
                        </div>
                        <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                          <p className="text-gray-400 text-sm uppercase">Hits</p>
                          <p className="text-2xl font-bold text-white">{careerStats.total_hits}</p>
                        </div>
                        <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                          <p className="text-gray-400 text-sm uppercase">Runs</p>
                          <p className="text-2xl font-bold text-white">{careerStats.total_runs}</p>
                        </div>
                        <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                          <p className="text-gray-400 text-sm uppercase">RBIs</p>
                          <p className="text-2xl font-bold text-white">{careerStats.total_rbis}</p>
                        </div>
                        <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                          <p className="text-gray-400 text-sm uppercase">Doubles</p>
                          <p className="text-2xl font-bold text-white">{careerStats.total_doubles}</p>
                        </div>
                        <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                          <p className="text-gray-400 text-sm uppercase">Triples</p>
                          <p className="text-2xl font-bold text-white">{careerStats.total_triples}</p>
                        </div>
                        <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                          <p className="text-gray-400 text-sm uppercase">Home Runs</p>
                          <p className="text-2xl font-bold text-white">{careerStats.total_home_runs}</p>
                        </div>
                        <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                          <p className="text-gray-400 text-sm uppercase">Walks</p>
                          <p className="text-2xl font-bold text-white">{careerStats.total_walks}</p>
                        </div>
                        <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                          <p className="text-gray-400 text-sm uppercase">Strikeouts</p>
                          <p className="text-2xl font-bold text-white">{careerStats.total_strikeouts}</p>
                        </div>
                        <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                          <p className="text-gray-400 text-sm uppercase">Stolen Bases</p>
                          <p className="text-2xl font-bold text-white">{careerStats.total_stolen_bases}</p>
                        </div>
                      </div>
                    </div>

                    {careerStats.total_innings_pitched > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">Pitching</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                          <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Innings Pitched</p>
                            <p className="text-2xl font-bold text-white">{Number(careerStats.total_innings_pitched).toFixed(1)}</p>
                          </div>
                          <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">ERA</p>
                            <p className="text-2xl font-bold text-white">{Number(careerStats.career_era).toFixed(2)}</p>
                          </div>
                          <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Earned Runs</p>
                            <p className="text-2xl font-bold text-white">{careerStats.total_earned_runs}</p>
                          </div>
                          <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Strikeouts</p>
                            <p className="text-2xl font-bold text-white">{careerStats.total_pitching_strikeouts}</p>
                          </div>
                          <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Walks Allowed</p>
                            <p className="text-2xl font-bold text-white">{careerStats.total_walks_allowed}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {(careerStats.total_putouts > 0 || careerStats.total_assists > 0) && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">Fielding</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                          <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Putouts</p>
                            <p className="text-2xl font-bold text-white">{careerStats.total_putouts}</p>
                          </div>
                          <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Assists</p>
                            <p className="text-2xl font-bold text-white">{careerStats.total_assists}</p>
                          </div>
                          <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Errors</p>
                            <p className="text-2xl font-bold text-white">{careerStats.total_errors}</p>
                          </div>
                          <div className="bg-[#1A1A1A]/60 backdrop-blur p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Fielding %</p>
                            <p className="text-2xl font-bold text-white">{Number(careerStats.career_fielding_percentage).toFixed(3)}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {playerStats.length > 0 && (() => {
                const s = playerStats[selectedSeasonIdx];
                return (
                  <div className="bg-[#222222] rounded-lg overflow-hidden shadow-lg border border-white/10 p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                      <div className="flex items-center gap-3">
                        <BarChart3 className="w-6 h-6 text-cardinal flex-shrink-0" />
                        <h2 className="text-2xl font-bold text-white">Season Statistics</h2>
                      </div>
                      {playerStats.length > 1 && (
                        <select
                          value={selectedSeasonIdx}
                          onChange={(e) => setSelectedSeasonIdx(Number(e.target.value))}
                          className="px-3 py-2 rounded-lg bg-[#1A1A1A] text-white border border-white/10 focus:border-cardinal focus:ring focus:ring-cardinal/30 text-sm font-medium"
                        >
                          {playerStats.map((stat, idx) => (
                            <option key={stat.id} value={idx}>
                              {stat.season} Season{idx === 0 ? ' (Latest)' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                      {playerStats.length === 1 && (
                        <span className="text-cardinal font-semibold">{s.season} Season</span>
                      )}
                    </div>

                    <div className="space-y-6">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">Batting</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                          <div className="bg-[#1A1A1A] p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Games</p>
                            <p className="text-2xl font-bold text-white">{s.games_played}</p>
                          </div>
                          <div className="bg-[#1A1A1A] p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">AVG</p>
                            <p className="text-2xl font-bold text-white">{s.batting_average?.toFixed(3) || '.000'}</p>
                          </div>
                          <div className="bg-[#1A1A1A] p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">At Bats</p>
                            <p className="text-2xl font-bold text-white">{s.at_bats}</p>
                          </div>
                          <div className="bg-[#1A1A1A] p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Hits</p>
                            <p className="text-2xl font-bold text-white">{s.hits}</p>
                          </div>
                          <div className="bg-[#1A1A1A] p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Runs</p>
                            <p className="text-2xl font-bold text-white">{s.runs}</p>
                          </div>
                          <div className="bg-[#1A1A1A] p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">RBIs</p>
                            <p className="text-2xl font-bold text-white">{s.rbis}</p>
                          </div>
                          <div className="bg-[#1A1A1A] p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Doubles</p>
                            <p className="text-2xl font-bold text-white">{s.doubles}</p>
                          </div>
                          <div className="bg-[#1A1A1A] p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Triples</p>
                            <p className="text-2xl font-bold text-white">{s.triples}</p>
                          </div>
                          <div className="bg-[#1A1A1A] p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Home Runs</p>
                            <p className="text-2xl font-bold text-white">{s.home_runs}</p>
                          </div>
                          <div className="bg-[#1A1A1A] p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Walks</p>
                            <p className="text-2xl font-bold text-white">{s.walks}</p>
                          </div>
                          <div className="bg-[#1A1A1A] p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Strikeouts</p>
                            <p className="text-2xl font-bold text-white">{s.strikeouts}</p>
                          </div>
                          <div className="bg-[#1A1A1A] p-4 rounded-lg">
                            <p className="text-gray-400 text-sm uppercase">Stolen Bases</p>
                            <p className="text-2xl font-bold text-white">{s.stolen_bases}</p>
                          </div>
                        </div>
                      </div>

                      {(s.innings_pitched > 0 || s.earned_runs > 0 || s.pitching_strikeouts > 0 || s.walks_allowed > 0) && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">Pitching</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                            <div className="bg-[#1A1A1A] p-4 rounded-lg">
                              <p className="text-gray-400 text-sm uppercase">Innings Pitched</p>
                              <p className="text-2xl font-bold text-white">{s.innings_pitched?.toFixed(1) || '0.0'}</p>
                            </div>
                            <div className="bg-[#1A1A1A] p-4 rounded-lg">
                              <p className="text-gray-400 text-sm uppercase">ERA</p>
                              <p className="text-2xl font-bold text-white">{s.era?.toFixed(2) || '0.00'}</p>
                            </div>
                            <div className="bg-[#1A1A1A] p-4 rounded-lg">
                              <p className="text-gray-400 text-sm uppercase">Earned Runs</p>
                              <p className="text-2xl font-bold text-white">{s.earned_runs || 0}</p>
                            </div>
                            <div className="bg-[#1A1A1A] p-4 rounded-lg">
                              <p className="text-gray-400 text-sm uppercase">Strikeouts</p>
                              <p className="text-2xl font-bold text-white">{s.pitching_strikeouts || 0}</p>
                            </div>
                            <div className="bg-[#1A1A1A] p-4 rounded-lg">
                              <p className="text-gray-400 text-sm uppercase">Walks Allowed</p>
                              <p className="text-2xl font-bold text-white">{s.walks_allowed || 0}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {(s.putouts > 0 || s.assists > 0 || s.errors >= 0) && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-400 uppercase mb-3">Fielding</h4>
                          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                            <div className="bg-[#1A1A1A] p-4 rounded-lg">
                              <p className="text-gray-400 text-sm uppercase">Putouts</p>
                              <p className="text-2xl font-bold text-white">{s.putouts || 0}</p>
                            </div>
                            <div className="bg-[#1A1A1A] p-4 rounded-lg">
                              <p className="text-gray-400 text-sm uppercase">Assists</p>
                              <p className="text-2xl font-bold text-white">{s.assists || 0}</p>
                            </div>
                            <div className="bg-[#1A1A1A] p-4 rounded-lg">
                              <p className="text-gray-400 text-sm uppercase">Errors</p>
                              <p className="text-2xl font-bold text-white">{s.errors || 0}</p>
                            </div>
                            <div className="bg-[#1A1A1A] p-4 rounded-lg">
                              <p className="text-gray-400 text-sm uppercase">Fielding %</p>
                              <p className="text-2xl font-bold text-white">{s.fielding_percentage?.toFixed(3) || '.000'}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PlayerDetails;